// cesium-performance.ts - Performance monitoring and optimization for Cesium

interface PerformanceMetrics {
  fps: number;
  frameTime: number;
  memoryUsage: number;
  tilesetCount: number;
  renderTime: number;
  cpuUsage: number;
}

interface PerformanceOptions {
  enableLogging?: boolean;
  logInterval?: number;
  enableAdaptiveQuality?: boolean;
  maxFps?: number;
  enablePerformanceMode?: boolean;
}

class CesiumPerformanceMonitor {
  private viewer: any = null;
  private options: PerformanceOptions;
  private metrics: PerformanceMetrics = {
    fps: 0,
    frameTime: 0,
    memoryUsage: 0,
    tilesetCount: 0,
    renderTime: 0,
    cpuUsage: 0
  };
  
  private frameCount = 0;
  private lastTime = performance.now();
  private logInterval: number | null = null;
  private performanceObserver: PerformanceObserver | null = null;
  private isMonitoring = false;
  private lastModeChange = 0;
  private currentMode: 'quality' | 'balanced' | 'performance' | 'emergency' = 'quality';
  private emergencyModeActive = false;
  
  constructor(options: PerformanceOptions = {}) {
    this.options = {
      enableLogging: true,
      logInterval: 2000, // Log every 2 seconds
      enableAdaptiveQuality: true,
      maxFps: 60,
      enablePerformanceMode: false,
      ...options
    };
  }

  initialize(viewer: any) {
    this.viewer = viewer;
    
    if (this.options.enableLogging) {
      this.startMonitoring();
    }

    // Apply initial performance optimizations
    this.applyPerformanceOptimizations();
    
    console.log('🚀 Cesium Performance Monitor initialized:', {
      logging: this.options.enableLogging,
      adaptiveQuality: this.options.enableAdaptiveQuality,
      performanceMode: this.options.enablePerformanceMode
    });
  }

  private startMonitoring() {
    if (this.isMonitoring) return;
    
    this.isMonitoring = true;
    
    // Setup frame monitoring
    this.viewer.scene.postRender.addEventListener(this.onFrameRender.bind(this));
    
    // Setup performance observer for paint timing
    if ('PerformanceObserver' in window) {
      try {
        this.performanceObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            if (entry.entryType === 'paint') {
              this.metrics.renderTime = entry.startTime;
            }
          });
        });
        this.performanceObserver.observe({ entryTypes: ['paint', 'measure'] });
      } catch (error) {
        console.warn('Performance Observer not supported:', error);
      }
    }

    // Start periodic logging
    if (this.options.logInterval) {
      this.logInterval = window.setInterval(() => {
        this.logPerformanceMetrics();
        
        // Apply adaptive quality if enabled
        if (this.options.enableAdaptiveQuality) {
          this.adaptQualityBasedOnPerformance();
        }
      }, this.options.logInterval);
    }
  }

  private onFrameRender() {
    this.frameCount++;
    const currentTime = performance.now();
    const deltaTime = currentTime - this.lastTime;
    
    if (deltaTime >= 1000) {
      this.metrics.fps = Math.round((this.frameCount * 1000) / deltaTime);
      this.metrics.frameTime = deltaTime / this.frameCount;
      this.frameCount = 0;
      this.lastTime = currentTime;
      
      // Update other metrics
      this.updateMetrics();
    }
  }

  private updateMetrics() {
    // Memory usage
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      this.metrics.memoryUsage = Math.round(memory.usedJSHeapSize / 1024 / 1024); // MB
    }

    // Tileset count
    if (this.viewer && this.viewer.scene && this.viewer.scene.primitives) {
      this.metrics.tilesetCount = this.viewer.scene.primitives.length;
    }

    // CPU usage estimation (based on frame time)
    this.metrics.cpuUsage = Math.min(100, Math.round(this.metrics.frameTime / 16.67 * 100));
  }

  private logPerformanceMetrics() {
    const performanceLevel = this.getPerformanceLevel();
    const emoji = this.getPerformanceEmoji(performanceLevel);
    
    console.group(`${emoji} Cesium Performance Metrics (${performanceLevel})`);
    console.log(`🎯 FPS: ${this.metrics.fps} (target: ${this.options.maxFps})`);
    console.log(`⏱️ Frame Time: ${this.metrics.frameTime.toFixed(2)}ms`);
    console.log(`🧠 Memory: ${this.metrics.memoryUsage}MB`);
    console.log(`📦 Tilesets: ${this.metrics.tilesetCount}`);
    console.log(`🏃 CPU Usage: ${this.metrics.cpuUsage}%`);
    
    // Add temperature warning if performance is poor
    if (performanceLevel === 'Poor') {
      console.warn('🔥 High CPU usage detected! Your laptop may be getting hot.');
      console.warn('💡 Consider enabling performance mode or reducing quality settings.');
    }
    
    console.groupEnd();
  }

  private getPerformanceLevel(): 'Excellent' | 'Good' | 'Fair' | 'Poor' {
    const fps = this.metrics.fps;
    const cpuUsage = this.metrics.cpuUsage;
    
    if (fps >= 50 && cpuUsage < 50) return 'Excellent';
    if (fps >= 30 && cpuUsage < 70) return 'Good';
    if (fps >= 20 && cpuUsage < 85) return 'Fair';
    return 'Poor';
  }

  private getPerformanceEmoji(level: string): string {
    switch (level) {
      case 'Excellent': return '🚀';
      case 'Good': return '✅';
      case 'Fair': return '⚠️';
      case 'Poor': return '🔥';
      default: return '📊';
    }
  }

  private adaptQualityBasedOnPerformance() {
    if (!this.viewer) return;
    
    const now = Date.now();
    const level = this.getPerformanceLevel();
    
    // Prevent mode switching too frequently (at least 5 seconds between changes)
    if (now - this.lastModeChange < 5000) return;
    
    // Check for emergency conditions (extremely poor performance)
    const isEmergency = this.metrics.fps < 10 && this.metrics.cpuUsage >= 95;
    
    switch (level) {
      case 'Poor':
        if (isEmergency && !this.emergencyModeActive) {
          this.enableEmergencyMode();
          console.log('🚨 EMERGENCY: Extremely poor performance - enabling emergency mode');
        } else if (this.currentMode !== 'performance' && this.currentMode !== 'emergency') {
          this.enablePerformanceMode();
          console.log('🔥 Poor performance detected - enabling performance mode');
        }
        break;
      case 'Fair':
        if (this.currentMode === 'quality') {
          this.enableBalancedMode();
          console.log('⚠️ Fair performance - applying balanced settings');
        }
        break;
      case 'Good':
        if (this.currentMode === 'emergency') {
          this.enablePerformanceMode();
          console.log('📈 Performance improved - switching from emergency to performance mode');
        }
        break;
      case 'Excellent':
        if (this.currentMode === 'performance' || this.currentMode === 'emergency') {
          this.enableBalancedMode();
          console.log('🚀 Excellent performance - upgrading to balanced mode');
        }
        break;
    }
    
    this.lastModeChange = now;
  }

  enablePerformanceMode() {
    if (!this.viewer) return;
    
    console.log('🔧 Enabling Performance Mode...');
    
    // Aggressive optimizations
    this.viewer.scene.globe.maximumScreenSpaceError = 16.0; // Higher = less detail
    this.viewer.resolutionScale = 0.5; // Lower resolution
    this.viewer.scene.postProcessStages.fxaa.enabled = false;
    this.viewer.scene.fog.enabled = false;
    this.viewer.scene.skyAtmosphere.show = false;
    this.viewer.scene.globe.enableLighting = false;
    this.viewer.scene.globe.depthTestAgainstTerrain = false;
    
    // Enable request render mode to reduce continuous rendering
    this.viewer.requestRenderMode = true;
    this.viewer.maximumRenderTimeChange = Infinity;
    
    // Reduce terrain quality
    if (this.viewer.terrainProvider) {
      this.viewer.scene.globe.terrainExaggeration = 0.5;
    }
    
    this.currentMode = 'performance';
    this.emergencyModeActive = false;
    this.options.enablePerformanceMode = true;
  }

  enableEmergencyMode() {
    if (!this.viewer) return;
    
    console.log('🚨 ENABLING EMERGENCY MODE - EXTREME PERFORMANCE OPTIMIZATIONS!');
    console.log('🔥 Your laptop is running very hot! Applying maximum optimizations...');
    
    // EXTREME optimizations for overheating laptops
    this.viewer.scene.globe.maximumScreenSpaceError = 32.0; // Very low detail
    this.viewer.resolutionScale = 0.25; // Quarter resolution
    this.viewer.scene.postProcessStages.fxaa.enabled = false;
    this.viewer.scene.fog.enabled = false;
    this.viewer.scene.skyAtmosphere.show = false;
    this.viewer.scene.globe.enableLighting = false;
    this.viewer.scene.globe.depthTestAgainstTerrain = false;
    
    // Force minimal rendering
    this.viewer.requestRenderMode = true;
    this.viewer.maximumRenderTimeChange = Infinity;
    this.viewer.targetFrameRate = 15; // Limit to 15 FPS max
    
    // Disable terrain completely if possible
    this.viewer.scene.globe.show = false;
    
    // Reduce tileset quality drastically
    if (this.viewer.scene.primitives) {
      for (let i = 0; i < this.viewer.scene.primitives.length; i++) {
        const primitive = this.viewer.scene.primitives.get(i);
        if (primitive && primitive.maximumScreenSpaceError !== undefined) {
          primitive.maximumScreenSpaceError = 64.0; // Extremely low detail
        }
      }
    }
    
    // Disable shadows completely
    this.viewer.shadows = false;
    
    this.currentMode = 'emergency';
    this.emergencyModeActive = true;
    
    console.log('🆘 Emergency mode active - if laptop is still hot, consider:');
    console.log('   • Closing the 3D view entirely');
    console.log('   • Reducing the number of loaded models');
    console.log('   • Using window.cesiumPerformance.unloadExcessTilesets()');
  }

  enableBalancedMode() {
    if (!this.viewer) return;
    
    console.log('⚖️ Enabling Balanced Mode...');
    
    // Moderate optimizations
    this.viewer.scene.globe.maximumScreenSpaceError = 4.0;
    this.viewer.resolutionScale = 0.75;
    this.viewer.scene.postProcessStages.fxaa.enabled = false; // Keep FXAA disabled
    this.viewer.scene.fog.enabled = true;
    this.viewer.scene.skyAtmosphere.show = true;
    this.viewer.scene.globe.enableLighting = true;
    this.viewer.scene.globe.show = true; // Re-enable globe if it was disabled
    
    // Semi-request render mode
    this.viewer.requestRenderMode = false;
    this.viewer.targetFrameRate = undefined; // Remove frame rate limit
    
    this.currentMode = 'balanced';
    this.emergencyModeActive = false;
  }

  enableQualityMode() {
    if (!this.viewer) return;
    
    console.log('✨ Enabling Quality Mode...');
    
    // Best visual quality
    this.viewer.scene.globe.maximumScreenSpaceError = 1.0;
    this.viewer.resolutionScale = 1.0;
    this.viewer.scene.postProcessStages.fxaa.enabled = true;
    this.viewer.scene.fog.enabled = true;
    this.viewer.scene.skyAtmosphere.show = true;
    this.viewer.scene.globe.enableLighting = true;
    this.viewer.scene.globe.depthTestAgainstTerrain = true;
    this.viewer.scene.globe.show = true; // Re-enable globe if it was disabled
    
    this.viewer.requestRenderMode = false;
    this.viewer.targetFrameRate = undefined; // Remove frame rate limit
    
    this.currentMode = 'quality';
    this.emergencyModeActive = false;
    this.options.enablePerformanceMode = false;
  }

  private applyPerformanceOptimizations() {
    if (!this.viewer) return;
    
    // Apply initial optimizations
    if (this.options.enablePerformanceMode) {
      this.enablePerformanceMode();
    }
    
    // Disable unnecessary features for better performance
    this.viewer.scene.screenSpaceCameraController.inertiaSpin = 0.0;
    this.viewer.scene.screenSpaceCameraController.inertiaTranslate = 0.0;
    this.viewer.scene.screenSpaceCameraController.inertiaZoom = 0.0;
  }

  getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }

  unloadExcessTilesets(maxTilesets: number = 3) {
    if (!this.viewer || !this.viewer.scene.primitives) return;
    
    const primitives = this.viewer.scene.primitives;
    let tilesetCount = 0;
    const tilesets: any[] = [];
    
    // Count and collect tilesets
    for (let i = 0; i < primitives.length; i++) {
      const primitive = primitives.get(i);
      if (primitive && primitive._assetId !== undefined) {
        tilesets.push(primitive);
        tilesetCount++;
      }
    }
    
    if (tilesetCount <= maxTilesets) {
      console.log(`📦 Only ${tilesetCount} tilesets loaded, no need to unload`);
      return;
    }
    
    const toUnload = tilesetCount - maxTilesets;
    console.log(`🗑️ Unloading ${toUnload} tilesets to improve performance (keeping ${maxTilesets})`);
    
    // Remove the last loaded tilesets (assuming they're less important)
    for (let i = tilesets.length - 1; i >= maxTilesets; i--) {
      const tileset = tilesets[i];
      try {
        primitives.remove(tileset);
        console.log(`✅ Unloaded tileset ${tileset._assetId || 'unknown'}`);
      } catch (error) {
        console.warn(`⚠️ Failed to unload tileset:`, error);
      }
    }
    
    // Force garbage collection if available
    if ((window as any).gc) {
      setTimeout(() => {
        (window as any).gc();
        console.log('🧹 Forced garbage collection');
      }, 1000);
    }
    
    console.log(`🎯 Reduced tilesets from ${tilesetCount} to ${maxTilesets}`);
  }

  showPerformanceReport() {
    const level = this.getPerformanceLevel();
    const emoji = this.getPerformanceEmoji(level);
    
    console.group(`${emoji} Cesium Performance Report`);
    console.table(this.metrics);
    
    if (level === 'Poor') {
      console.group('🔥 Performance Recommendations');
      console.log('• Enable Performance Mode: performanceMonitor.enablePerformanceMode()');
      console.log('• Reduce number of loaded tilesets');
      console.log('• Close other browser tabs');
      console.log('• Check for background processes');
      console.groupEnd();
    }
    
    console.groupEnd();
    
    return {
      level,
      metrics: this.metrics,
      recommendations: level === 'Poor' ? [
        'Enable Performance Mode',
        'Reduce loaded tilesets',
        'Close other tabs',
        'Check background processes'
      ] : []
    };
  }

  destroy() {
    this.isMonitoring = false;
    
    if (this.logInterval) {
      clearInterval(this.logInterval);
      this.logInterval = null;
    }
    
    if (this.performanceObserver) {
      this.performanceObserver.disconnect();
      this.performanceObserver = null;
    }
    
    console.log('🛑 Cesium Performance Monitor destroyed');
  }
}

// Export singleton instance
export const performanceMonitor = new CesiumPerformanceMonitor();

// Export class for custom instances
export { CesiumPerformanceMonitor };

// Export types
export type { PerformanceMetrics, PerformanceOptions }; 